import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, GripVertical, Eye, Save, XCircle, Upload, Image } from 'lucide-react';
import { FORM_FIELD_TYPES } from '../config';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { OPTION_CURRENCIES } from '../utils/pricing';
import { emptyCategory, emptyPass, passId, passIdFor, withUniquePassIds } from '../utils/passes';
import { clearFormDraft, readFormDraft, writeFormDraft } from '../utils/formDraft';
import { useToast } from './Toast';
import PriceScheduleEditor from './PriceScheduleEditor';
import { IMAGE_LIMITS, resizeImage } from '../utils/imageResize';

let optionSeq = 0;
const optionId = () => `opt_${Date.now().toString(36)}_${(optionSeq++).toString(36)}`;
const withOptionIds = (fields = []) => (fields || []).map((field) => {
  const options = field.optionsWithPricing;
  if (!options?.length) return field;
  return {
    ...field,
    optionsWithPricing: options.map((opt) => ({
      ...opt,
      id: opt.id || optionId(),
      subFields: withOptionIds(opt.subFields || []),
    })),
  };
});

const FormBuilder = ({ 
  initialSchema = { name: '', fields: [] }, 
  onSave, 
  onPreview,
  eventId = null,
  className = "",
  hideMainSaveButton = false, // Hide the bottom "Save Schema" button when editing events
  autoSaveOnFieldEdit = false, // Whether "Done" button should trigger onSave (for EventManagement)
  draftKey = null, // When set, unsaved edits stay in this browser across refresh
}) => {
  const storedDraft = draftKey ? readFormDraft(draftKey) : null;
  const keepDraft = useRef(!!storedDraft);
  const forceApply = useRef(false);
  const savedSnapshot = useRef(null);
  const [schema, setSchema] = useState(storedDraft || initialSchema);
  const [draftState, setDraftState] = useState(storedDraft ? 'restored' : null);
  const [draftReset, setDraftReset] = useState(0);
  const [editingField, setEditingField] = useState(null);
  const [expandedOptions, setExpandedOptions] = useState({});

  const getCurrentOptions = (field) =>
    field.optionsWithPricing ||
    field.optionWithBlackLists ||
    field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) ||
    [];

  const syncOptionsForField = (field, options) => {
    const filteredOptions = options.filter(opt => opt.label && opt.label.trim() !== '');
    const optionsForPricing = options.map(opt => ({
      ...opt,
      blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
      subFields: (opt.subFields || []).map(sub => ({ ...sub }))
    }));
    const optionsForBlacklist = options.map(opt => ({
      ...opt,
      blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
      subFields: (opt.subFields || []).map(sub => ({ ...sub }))
    }));

    updateField(field.id, {
      optionsWithPricing: optionsForPricing,
      optionWithBlackLists: optionsForBlacklist,
      options: filteredOptions.map(opt => opt.label)
    });
  };

  // Sync schema state when initialSchema content changes (preserve formLogoUrl/formBackgroundUrl from current state if not in initialSchema)
  const initialJson = JSON.stringify(initialSchema);
  useEffect(() => {
    if (keepDraft.current && !forceApply.current) {
      keepDraft.current = false;
      savedSnapshot.current = JSON.stringify(initialSchema);
      setEditingField(null);
      return;
    }
    forceApply.current = false;
    // Deep clone the initialSchema to prevent reference sharing
    setSchema(prev => {
      const deepClonedSchema = {
        ...initialSchema,
        formLogoUrl: initialSchema.formLogoUrl ?? prev.formLogoUrl,
        formBackgroundUrl: initialSchema.formBackgroundUrl ?? prev.formBackgroundUrl,
        passes: JSON.parse(JSON.stringify(initialSchema.passes || [])),
        groups: JSON.parse(JSON.stringify(initialSchema.groups || [])),
        fields: initialSchema.fields?.map((field, index) => {
        const clonedField = { ...field };

        // Ensure every field has a stable, unique id
        // This is CRITICAL because updateField relies on field.id to target a single field.
        // Backend formSchema doesn't contain ids, so without this all fields would have `undefined` id
        // and every update would apply to all of them.
        if (!clonedField.id) {
          clonedField.id = `field_${index}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        }
        
        // Deep clone option arrays for dropdown/radio fields
        if (field.type === 'dropdown' || field.type === 'radio') {
          if (field.optionsWithPricing) {
            clonedField.optionsWithPricing = field.optionsWithPricing.map(opt => ({
              ...opt,
              id: opt.id || optionId(),
              blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
              subFields: (opt.subFields || []).map(sub => ({ ...sub }))
            }));
          }
          
          if (field.optionWithBlackLists) {
            clonedField.optionWithBlackLists = field.optionWithBlackLists.map(opt => ({
              ...opt,
              blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
              subFields: (opt.subFields || []).map(sub => ({ ...sub }))
            }));
          }
          
          if (field.options && Array.isArray(field.options)) {
            clonedField.options = [...field.options];
          }
        }
        
        // Deep clone blacklist array at field level
        if (field.blackedListedQrPurposes) {
          clonedField.blackedListedQrPurposes = [...field.blackedListedQrPurposes];
        }
        
        return clonedField;
      }) || []
      };
      const next = withUniquePassIds(deepClonedSchema);
      if (!next.sections?.length) {
        next.sections = [...new Set((next.fields || []).map((field) => field.section).filter(Boolean))];
      }
      savedSnapshot.current = JSON.stringify(next);
      return next;
    });
    setEditingField(null); // Reset editing state when schema changes
  }, [initialJson, draftReset]);

  useEffect(() => {
    if (!draftKey) return undefined;
    const flush = () => {
      if (!savedSnapshot.current) return;
      if (JSON.stringify(schema) === savedSnapshot.current) {
        if (readFormDraft(draftKey)) clearFormDraft(draftKey);
        setDraftState((current) => (current === 'saved' ? null : current));
        return;
      }
      try {
        writeFormDraft(draftKey, schema);
        setDraftState('saved');
      } catch {
        setDraftState('error');
      }
    };
    const timer = setTimeout(flush, 400);
    window.addEventListener('pagehide', flush);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pagehide', flush);
    };
  }, [schema, draftKey]);

  // Fetch QR scan purposes for the event
  const { data: qrScanPurposes = [] } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
    enabled: !!eventId,
  });

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      label: `New ${type} Field`,
      required: false,
      placeholder: `Enter ${type}`,
      options: type === 'dropdown' || type === 'radio' ? ['Option 1', 'Option 2'] : undefined,
      accept: type === 'file' ? '*' : undefined,
      blackedListedQrPurposes: [],
      price: type === 'list' ? 0 : undefined
    };

    setSchema(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  // Replaces one option's properties, keeping the pricing and blacklist copies of the options in sync.
  const patchOption = (field, index, patch) => {
    const currentOptions = field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || [];
    const newOptions = currentOptions.map((opt, i) => ({
      ...opt,
      ...(i === index ? patch : {}),
      blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
    }));
    updateField(field.id, {
      optionsWithPricing: newOptions.map(opt => ({ ...opt, blackListedQrPurposes: [...opt.blackListedQrPurposes] })),
      optionWithBlackLists: newOptions.map(opt => ({ ...opt, blackListedQrPurposes: [...opt.blackListedQrPurposes] })),
      options: newOptions.filter(opt => opt.label && opt.label.trim() !== '').map(opt => opt.label),
    });
  };

  const updateField = (fieldId, updates) => {
    
    setSchema(prev => {
      
      const newFields = prev.fields.map(field => {
        if (field.id !== fieldId) {
          // For fields that are NOT being updated, ensure they keep their own references
          // by returning a completely new object with deeply cloned arrays
          if (field.type === 'dropdown' || field.type === 'radio') {
            return {
              ...field,
              options: field.options ? [...field.options] : undefined,
              optionsWithPricing: field.optionsWithPricing ? field.optionsWithPricing.map(opt => ({
                ...opt,
                blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
                subFields: (opt.subFields || []).map(sub => ({ ...sub }))
              })) : undefined,
              optionWithBlackLists: field.optionWithBlackLists ? field.optionWithBlackLists.map(opt => ({
                ...opt,
                blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
                subFields: (opt.subFields || []).map(sub => ({ ...sub }))
              })) : undefined,
              blackedListedQrPurposes: field.blackedListedQrPurposes ? [...field.blackedListedQrPurposes] : []
            };
          }
          return field;
        }
        
        // For the field being updated
        
        // Safety check: don't allow options/pricing on non-dropdown fields
        const cleanedUpdates = { ...updates };
        if (field.type !== 'dropdown' && field.type !== 'radio') {
          delete cleanedUpdates.options;
          delete cleanedUpdates.optionsWithPricing;
          delete cleanedUpdates.optionWithBlackLists;
        }
        
        // Deep clone option arrays to prevent reference sharing between properties
        if (cleanedUpdates.optionsWithPricing) {
          cleanedUpdates.optionsWithPricing = cleanedUpdates.optionsWithPricing.map(opt => ({
            ...opt,
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
            subFields: (opt.subFields || []).map(sub => ({ ...sub }))
          }));
        }
        
        if (cleanedUpdates.optionWithBlackLists) {
          cleanedUpdates.optionWithBlackLists = cleanedUpdates.optionWithBlackLists.map(opt => ({
            ...opt,
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [],
            subFields: (opt.subFields || []).map(sub => ({ ...sub }))
          }));
        }
        
        if (cleanedUpdates.options && Array.isArray(cleanedUpdates.options)) {
          cleanedUpdates.options = [...cleanedUpdates.options];
        }
        
        
        return { ...field, ...cleanedUpdates };
      });
      
      console.log('📤 New schema fields:', newFields.map(f => ({ 
        id: f.id, 
        label: f.label, 
        optionsCount: f.options?.length,
        options: f.options
      })));
      
      return { ...prev, fields: newFields };
    });
  };

  const removeField = (fieldId) => {
    setSchema(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }));
    setEditingField(null);
  };

  const moveField = (fromIndex, toIndex) => {
    setSchema(prev => {
      const newFields = [...prev.fields];
      const [movedField] = newFields.splice(fromIndex, 1);
      newFields.splice(toIndex, 0, movedField);
      return { ...prev, fields: newFields };
    });
  };

  const updateSchemaName = (name) => {
    setSchema(prev => ({ ...prev, name }));
  };

  const updateSchemaFormLogoUrl = (formLogoUrl) => {
    setSchema(prev => ({ ...prev, formLogoUrl: formLogoUrl || undefined }));
  };

  const updateSchemaFormBackgroundUrl = (formBackgroundUrl) => {
    setSchema(prev => ({ ...prev, formBackgroundUrl: formBackgroundUrl || undefined }));
  };

  const [uploadingAsset, setUploadingAsset] = useState(null);
  const { showSuccess, showError } = useToast();

  const handleFormSchemaAssetUpload = async (assetType, file) => {
    if (!file) return;
    setUploadingAsset(assetType);
    try {
      const url = await ApiService.uploadFormSchemaAsset(assetType, await resizeImage(file, IMAGE_LIMITS[assetType] || {}));
      setSchema(prev => {
        const updated = assetType === 'form_logo'
          ? { ...prev, formLogoUrl: url }
          : assetType === 'form_background'
            ? { ...prev, formBackgroundUrl: url }
            : prev;
        if (onSave && updated !== prev) onSave(updated);
        return updated;
      });
      showSuccess('Image uploaded successfully!');
    } catch (err) {
      showError(err.message || 'Upload failed');
    } finally {
      setUploadingAsset(null);
    }
  };

  const nextItemId = (item, name) => {
    const slug = passId({ name });
    const previousSlug = passId({ name: item.name });
    const stable = item.id && item.id !== previousSlug && item.id !== 'pass';
    return stable ? item.id : (name.trim() ? slug : item.id);
  };

  const nextPassId = (passes, index, name, groupId) => (
    name.trim() ? passIdFor(passes, index, name, groupId) : (passes[index]?.id || '')
  );

  const commitSchema = (produce) => {
    setSchema((prev) => {
      const next = produce(prev);
      if (autoSaveOnFieldEdit && onSave) onSave(next);
      return next;
    });
  };

  const handleSave = () => {
    if (onSave) {
      const next = withUniquePassIds({ ...schema, fields: withOptionIds(schema.fields) });
      const groups = next.groups || [];
      next.passes = (next.passes || []).map((pass) => {
        const parent = groups.find((group) => passId(group) === pass.groupId);
        if (!parent) return pass;
        const type = parent.type || 'PAID';
        return { ...pass, type, price: type === 'PAID' ? (Number(pass.price) || 0) : 0 };
      });
      setSchema(next);
      onSave(next);
    }
  };

  const discardDraft = () => {
    clearFormDraft(draftKey);
    forceApply.current = true;
    setDraftState(null);
    setDraftReset((n) => n + 1);
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview(schema);
    }
  };

  const renderFieldEditor = (field) => {
    if (editingField !== field.id) return null;

    return (
      <Card className="mt-2 border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Field Label
              </label>
              <Input
                value={field.label}
                onChange={(e) => updateField(field.id, { label: e.target.value })}
                placeholder="Enter field label"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder Text
              </label>
              <Input
                value={field.placeholder || ''}
                onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                placeholder="Enter placeholder text"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder Image (optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">If set, shows image instead of placeholder text in the input.</p>
              <div className="flex gap-2 items-center">
                <Input
                  value={field.placeholderImageUrl || ''}
                  onChange={(e) => updateField(field.id, { placeholderImageUrl: e.target.value || undefined })}
                  placeholder="URL or upload"
                  className="flex-1"
                />
                <label className="flex items-center px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm shrink-0">
                  <Upload className="w-4 h-4 mr-1" />
                  {uploadingAsset === `placeholder_${field.id}` ? '...' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setUploadingAsset(`placeholder_${field.id}`);
                        try {
                          const url = await ApiService.uploadFormSchemaAsset('placeholder_image', await resizeImage(f));
                          updateField(field.id, { placeholderImageUrl: url });
                          showSuccess('Placeholder image uploaded!');
                        } catch (err) {
                          showError(err.message || 'Upload failed');
                        } finally {
                          setUploadingAsset(null);
                        }
                      }
                      e.target.value = '';
                    }}
                    disabled={!!uploadingAsset}
                  />
                </label>
              </div>
              {field.placeholderImageUrl && (
                <div className="mt-2">
                  <img src={field.placeholderImageUrl} alt="Placeholder" className="max-h-12 object-contain rounded border" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
              <select
                aria-label="Section"
                value={field.section || ''}
                onChange={(e) => updateField(field.id, { section: e.target.value })}
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
              >
                <option value="">First step</option>
                {(schema.sections || []).filter(Boolean).map((section) => (
                  <option key={section} value={section}>{section}</option>
                ))}
                {field.section && !(schema.sections || []).includes(field.section) && (
                  <option value={field.section}>{field.section}</option>
                )}
              </select>
              <p className="mt-1 text-xs text-gray-500">Pick a section defined at the top of the form. The same section is one step. First step is used when none is selected.</p>
            </div>
            {(schema.passes || []).some((pass) => pass.name) && (
              <div>
                <p className="text-sm font-medium text-gray-700">Categories that ask for this</p>
                <p className="mb-1 text-xs text-gray-500">Leave all unticked to ask every category.</p>
                <div className="space-y-2">
                  {(schema.groups || []).filter((group) => group.name).map((group) => {
                    const categories = (schema.passes || []).filter((pass) => pass.name && pass.groupId === passId(group));
                    if (!categories.length) return null;
                    return (
                      <div key={passId(group)}>
                        <p className="text-xs font-medium text-gray-500">{group.name}</p>
                        <div className="mt-1 flex flex-wrap gap-3">
                          {categories.map((pass) => {
                            const id = passId(pass);
                            const checked = (field.passIds || []).includes(id);
                            return (
                              <label key={id} className="flex items-center gap-1.5 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => updateField(field.id, {
                                    passIds: e.target.checked
                                      ? [...(field.passIds || []), id]
                                      : (field.passIds || []).filter((item) => item !== id),
                                  })}
                                />
                                {pass.name}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`required-${field.id}`}
                checked={field.required}
                onChange={(e) => updateField(field.id, { required: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor={`required-${field.id}`} className="text-sm text-gray-700">
                Required field
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`analytics-${field.id}`}
                checked={!!field.trackAnalytics}
                onChange={(e) => updateField(field.id, { trackAnalytics: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor={`analytics-${field.id}`} className="text-sm text-gray-700">
                Track in analytics dashboard
              </label>
            </div>

            {(field.type === 'dropdown' || field.type === 'radio') && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Options Configuration
                  </label>
                  <div className="space-y-3">
                    {(field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || []).map((option, index) => {
                      const optionKey = `${field.id}-${index}`;
                      const isExpanded = expandedOptions[optionKey];
                      
                      return (
                        <div key={index} className="border rounded-lg bg-gray-50">
                          <div className="flex items-center space-x-3 p-3">
                        <div className="flex-1">
                          <Input
                            value={option.label || ''}
                            onChange={(e) => {
                                  const currentOptions = field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || [];
                                  const newOptions = currentOptions.map((opt, i) => 
                                    i === index ? { ...opt, label: e.target.value } : { ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }
                                  );
                              const filteredOptions = newOptions.filter(opt => opt.label && opt.label.trim() !== '');
                                  
                                  // Create completely independent copies for each property
                                  const optionsForPricing = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                  const optionsForBlacklist = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                  
                              updateField(field.id, { 
                                    optionsWithPricing: optionsForPricing,
                                    optionWithBlackLists: optionsForBlacklist,
                                    options: filteredOptions.map(opt => opt.label)
                              });
                            }}
                            placeholder="Option label"
                            className="text-sm"
                          />
                        </div>
                        <div className="w-24">
                          <Input
                            type="number"
                            value={option.price || 0}
                            onChange={(e) => {
                                  const currentOptions = field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || [];
                                  const newOptions = currentOptions.map((opt, i) => 
                                    i === index ? { ...opt, price: parseFloat(e.target.value) || 0 } : { ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }
                                  );
                              const filteredOptions = newOptions.filter(opt => opt.label && opt.label.trim() !== '');
                                  
                                  // Create completely independent copies for each property
                                  const optionsForPricing = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                  const optionsForBlacklist = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                  
                              updateField(field.id, { 
                                    optionsWithPricing: optionsForPricing,
                                    optionWithBlackLists: optionsForBlacklist,
                                    options: filteredOptions.map(opt => opt.label)
                              });
                            }}
                            placeholder="Price"
                            className="text-sm"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="w-24">
                          <select
                            aria-label="Currency"
                            value={option.currency || 'INR'}
                            onChange={(e) => patchOption(field, index, { currency: e.target.value })}
                            className="h-9 w-full rounded-md border border-gray-300 bg-white px-1 text-sm"
                          >
                            {OPTION_CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
                          </select>
                        </div>
                        <div className="w-24">
                          <Input
                            type="number"
                            value={option.slots ?? ''}
                            onChange={(e) => {
                                  const currentOptions = field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || [];
                                  const parsedSlots = e.target.value === '' ? null : Math.max(parseInt(e.target.value, 10) || 0, 0);
                                  const newOptions = currentOptions.map((opt, i) =>
                                    i === index ? { ...opt, slots: parsedSlots } : { ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }
                                  );
                              const filteredOptions = newOptions.filter(opt => opt.label && opt.label.trim() !== '');
                                  const optionsForPricing = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                  const optionsForBlacklist = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                              updateField(field.id, {
                                    optionsWithPricing: optionsForPricing,
                                    optionWithBlackLists: optionsForBlacklist,
                                    options: filteredOptions.map(opt => opt.label)
                              });
                            }}
                            placeholder="Slots"
                            className="text-sm"
                            min="0"
                            step="1"
                          />
                        </div>
                        <div className="text-xs text-gray-500 w-12">
                          {option.price > 0 ? 'Paid' : 'Free'}
                        </div>
                            
                            {/* Blacklist toggle button */}
                            {qrScanPurposes.length > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setExpandedOptions(prev => ({
                                    ...prev,
                                    [optionKey]: !prev[optionKey]
                                  }));
                                }}
                                className="text-xs"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Blacklist ({(option.blackListedQrPurposes || []).length})
                              </Button>
                            )}

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setExpandedOptions(prev => ({
                                  ...prev,
                                  [`${optionKey}-subfields`]: !prev[`${optionKey}-subfields`]
                                }));
                              }}
                              className="text-xs"
                            >
                              Sub-fields ({(option.subFields || []).length})
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setExpandedOptions(prev => ({
                                  ...prev,
                                  [`${optionKey}-prices`]: !prev[`${optionKey}-prices`]
                                }));
                              }}
                              className="text-xs"
                              title="Early-bird or late prices that switch automatically on a date"
                            >
                              Dates ({(option.priceSchedule || []).length})
                            </Button>
                            
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                                        const currentOptions = field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || [];
                                const newOptions = currentOptions.filter((_, i) => i !== index).map(opt => ({ 
                                  ...opt, 
                                  blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
                                }));
                            const filteredOptions = newOptions.filter(opt => opt.label && opt.label.trim() !== '');
                                
                                // Create completely independent copies for each property
                                const optionsForPricing = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                const optionsForBlacklist = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                
                            updateField(field.id, { 
                                  optionsWithPricing: optionsForPricing,
                                  optionWithBlackLists: optionsForBlacklist,
                              options: filteredOptions.map(opt => opt.label)
                            });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                          
                          {expandedOptions[`${optionKey}-prices`] && (
                            <PriceScheduleEditor
                              option={option}
                              onChange={(priceSchedule) => patchOption(field, index, { priceSchedule })}
                            />
                          )}

                          {/* Blacklist configuration panel */}
                          {isExpanded && qrScanPurposes.length > 0 && (
                            <div className="px-3 pb-3 pt-0 border-t bg-white">
                              <p className="text-xs text-gray-600 mb-2 mt-2">
                                Blacklist QR purposes for "{option.label}"
                              </p>
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {qrScanPurposes.map((purpose) => {
                                  const isChecked = (option.blackListedQrPurposes || []).includes(purpose.purpose);
                                  
                                  return (
                                    <div key={purpose.purpose} className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        id={`option-blacklist-${field.id}-${index}-${purpose.purpose}`}
                                        checked={isChecked}
                                      onChange={(e) => {
                                        const currentOptions = field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || [];
                                        const newOptions = currentOptions.map((opt, i) => {
                                          if (i !== index) return { ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] };
                                          
                                          const currentBlacklist = opt.blackListedQrPurposes || [];
                                          return {
                                            ...opt,
                                            blackListedQrPurposes: e.target.checked
                                              ? [...currentBlacklist, purpose.purpose]
                                              : currentBlacklist.filter(p => p !== purpose.purpose)
                                          };
                                        });
                                        
                                        // Create completely independent copies for each property
                                        const optionsForPricing = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                        const optionsForBlacklist = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                                        
                                        updateField(field.id, { 
                                          optionsWithPricing: optionsForPricing,
                                          optionWithBlackLists: optionsForBlacklist,
                                          options: newOptions.map(opt => opt.label)
                                        });
                                      }}
                                      className="rounded border-gray-300"
                                    />
                                    <label
                                      htmlFor={`option-blacklist-${field.id}-${index}-${purpose.purpose}`}
                                      className="text-xs text-gray-700 flex-1 cursor-pointer"
                                    >
                                      <span className="font-medium">{purpose.name}</span>
                                      <span className="text-gray-500 ml-1">({purpose.purpose})</span>
                                    </label>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {expandedOptions[`${optionKey}-subfields`] && (
                            <div className="px-3 pb-3 pt-0 border-t bg-white">
                              <p className="text-xs text-gray-600 mb-2 mt-2">
                                Sub-fields shown when "{option.label}" is selected
                              </p>
                              <div className="space-y-3">
                                {(option.subFields || []).map((subField, subIndex) => (
                                  <div key={`${optionKey}-sub-${subIndex}`} className="border rounded-lg p-3 bg-gray-50 space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                      <Input
                                        value={subField.label || ''}
                                        onChange={(e) => {
                                          const currentOptions = getCurrentOptions(field);
                                          const nextOptions = currentOptions.map((opt, i) => {
                                            if (i !== index) return { ...opt };
                                            const subFields = [...(opt.subFields || [])];
                                            subFields[subIndex] = { ...subField, label: e.target.value };
                                            return { ...opt, subFields };
                                          });
                                          syncOptionsForField(field, nextOptions);
                                        }}
                                        placeholder="Sub-field label"
                                        className="text-sm"
                                      />
                                      <Select
                                        value={subField.type || 'text'}
                                        onValueChange={(value) => {
                                          const currentOptions = getCurrentOptions(field);
                                          const nextOptions = currentOptions.map((opt, i) => {
                                            if (i !== index) return { ...opt };
                                            const subFields = [...(opt.subFields || [])];
                                            const nextSubField = { ...subField, type: value };
                                            if (value !== 'dropdown' && value !== 'radio') {
                                              delete nextSubField.optionsWithPricing;
                                              delete nextSubField.options;
                                            } else {
                                              nextSubField.optionsWithPricing = nextSubField.optionsWithPricing || [];
                                              nextSubField.options = (nextSubField.optionsWithPricing || []).map(o => o.label);
                                            }
                                            if (value === 'file') {
                                              nextSubField.accept = nextSubField.accept || '*';
                                            } else {
                                              delete nextSubField.accept;
                                            }
                                            subFields[subIndex] = nextSubField;
                                            return { ...opt, subFields };
                                          });
                                          syncOptionsForField(field, nextOptions);
                                        }}
                                      >
                                        <SelectTrigger className="text-sm">
                                          <SelectValue placeholder="Type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="text">Text</SelectItem>
                                          <SelectItem value="email">Email</SelectItem>
                                          <SelectItem value="number">Number</SelectItem>
                                          <SelectItem value="date">Date</SelectItem>
                                          <SelectItem value="textarea">Textarea</SelectItem>
                                          <SelectItem value="dropdown">Dropdown</SelectItem>
                                          <SelectItem value="file">File</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <Input
                                        value={subField.placeholder || ''}
                                        onChange={(e) => {
                                          const currentOptions = getCurrentOptions(field);
                                          const nextOptions = currentOptions.map((opt, i) => {
                                            if (i !== index) return { ...opt };
                                            const subFields = [...(opt.subFields || [])];
                                            subFields[subIndex] = { ...subField, placeholder: e.target.value };
                                            return { ...opt, subFields };
                                          });
                                          syncOptionsForField(field, nextOptions);
                                        }}
                                        placeholder="Placeholder"
                                        className="text-sm"
                                      />
                                    </div>

                                    {subField.type === 'file' && (
                                      <div>
                                        <label className="block text-xs text-gray-700 mb-1">
                                          Accepted File Types
                                        </label>
                                        <Input
                                          value={subField.accept || ''}
                                          onChange={(e) => {
                                            const currentOptions = getCurrentOptions(field);
                                            const nextOptions = currentOptions.map((opt, i) => {
                                              if (i !== index) return { ...opt };
                                              const subFields = [...(opt.subFields || [])];
                                              subFields[subIndex] = { ...subField, accept: e.target.value };
                                              return { ...opt, subFields };
                                            });
                                            syncOptionsForField(field, nextOptions);
                                          }}
                                          placeholder="e.g., image/*, .pdf, .doc"
                                          className="text-sm"
                                        />
                                      </div>
                                    )}

                                    <div className="flex items-center justify-between">
                                      <label className="text-xs text-gray-700 flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={!!subField.required}
                                          onChange={(e) => {
                                            const currentOptions = getCurrentOptions(field);
                                            const nextOptions = currentOptions.map((opt, i) => {
                                              if (i !== index) return { ...opt };
                                              const subFields = [...(opt.subFields || [])];
                                              subFields[subIndex] = { ...subField, required: e.target.checked };
                                              return { ...opt, subFields };
                                            });
                                            syncOptionsForField(field, nextOptions);
                                          }}
                                        />
                                        Required
                                      </label>
                                      <label className="text-xs text-gray-700 flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={!!subField.trackAnalytics}
                                          onChange={(e) => {
                                            const currentOptions = getCurrentOptions(field);
                                            const nextOptions = currentOptions.map((opt, i) => {
                                              if (i !== index) return { ...opt };
                                              const subFields = [...(opt.subFields || [])];
                                              subFields[subIndex] = { ...subField, trackAnalytics: e.target.checked };
                                              return { ...opt, subFields };
                                            });
                                            syncOptionsForField(field, nextOptions);
                                          }}
                                        />
                                        Analytics
                                      </label>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const currentOptions = getCurrentOptions(field);
                                          const nextOptions = currentOptions.map((opt, i) => {
                                            if (i !== index) return { ...opt };
                                            const subFields = (opt.subFields || []).filter((_, idx) => idx !== subIndex);
                                            return { ...opt, subFields };
                                          });
                                          syncOptionsForField(field, nextOptions);
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>

                                    {(subField.type === 'dropdown' || subField.type === 'radio') && (
                                      <div className="border rounded-md p-2 bg-white space-y-2">
                                        <p className="text-xs text-gray-600">Sub-options</p>
                                        {(subField.optionsWithPricing || []).map((subOption, subOptionIndex) => (
                                          <div key={`${optionKey}-subopt-${subOptionIndex}`} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                            <Input
                                              value={subOption.label || ''}
                                              onChange={(e) => {
                                                const currentOptions = getCurrentOptions(field);
                                                const nextOptions = currentOptions.map((opt, i) => {
                                                  if (i !== index) return { ...opt };
                                                  const subFields = [...(opt.subFields || [])];
                                                  const currentSubField = { ...(subFields[subIndex] || {}) };
                                                  const subOptions = [...(currentSubField.optionsWithPricing || [])];
                                                  subOptions[subOptionIndex] = { ...subOption, label: e.target.value };
                                                  currentSubField.optionsWithPricing = subOptions;
                                                  currentSubField.options = subOptions
                                                    .filter(o => o.label && o.label.trim() !== '')
                                                    .map(o => o.label);
                                                  subFields[subIndex] = currentSubField;
                                                  return { ...opt, subFields };
                                                });
                                                syncOptionsForField(field, nextOptions);
                                              }}
                                              placeholder="Label"
                                              className="text-sm"
                                            />
                                            <Input
                                              type="number"
                                              value={subOption.price || 0}
                                              onChange={(e) => {
                                                const currentOptions = getCurrentOptions(field);
                                                const nextOptions = currentOptions.map((opt, i) => {
                                                  if (i !== index) return { ...opt };
                                                  const subFields = [...(opt.subFields || [])];
                                                  const currentSubField = { ...(subFields[subIndex] || {}) };
                                                  const subOptions = [...(currentSubField.optionsWithPricing || [])];
                                                  subOptions[subOptionIndex] = { ...subOption, price: parseFloat(e.target.value) || 0 };
                                                  currentSubField.optionsWithPricing = subOptions;
                                                  currentSubField.options = subOptions
                                                    .filter(o => o.label && o.label.trim() !== '')
                                                    .map(o => o.label);
                                                  subFields[subIndex] = currentSubField;
                                                  return { ...opt, subFields };
                                                });
                                                syncOptionsForField(field, nextOptions);
                                              }}
                                              placeholder="Price"
                                              className="text-sm"
                                              min="0"
                                              step="0.01"
                                            />
                                            <Input
                                              type="number"
                                              value={subOption.slots ?? ''}
                                              onChange={(e) => {
                                                const currentOptions = getCurrentOptions(field);
                                                const parsedSlots = e.target.value === '' ? null : Math.max(parseInt(e.target.value, 10) || 0, 0);
                                                const nextOptions = currentOptions.map((opt, i) => {
                                                  if (i !== index) return { ...opt };
                                                  const subFields = [...(opt.subFields || [])];
                                                  const currentSubField = { ...(subFields[subIndex] || {}) };
                                                  const subOptions = [...(currentSubField.optionsWithPricing || [])];
                                                  subOptions[subOptionIndex] = { ...subOption, slots: parsedSlots };
                                                  currentSubField.optionsWithPricing = subOptions;
                                                  currentSubField.options = subOptions
                                                    .filter(o => o.label && o.label.trim() !== '')
                                                    .map(o => o.label);
                                                  subFields[subIndex] = currentSubField;
                                                  return { ...opt, subFields };
                                                });
                                                syncOptionsForField(field, nextOptions);
                                              }}
                                              placeholder="Slots"
                                              className="text-sm"
                                              min="0"
                                              step="1"
                                            />
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => {
                                                const currentOptions = getCurrentOptions(field);
                                                const nextOptions = currentOptions.map((opt, i) => {
                                                  if (i !== index) return { ...opt };
                                                  const subFields = [...(opt.subFields || [])];
                                                  const currentSubField = { ...(subFields[subIndex] || {}) };
                                                  const subOptions = (currentSubField.optionsWithPricing || []).filter((_, idx) => idx !== subOptionIndex);
                                                  currentSubField.optionsWithPricing = subOptions;
                                                  currentSubField.options = subOptions
                                                    .filter(o => o.label && o.label.trim() !== '')
                                                    .map(o => o.label);
                                                  subFields[subIndex] = currentSubField;
                                                  return { ...opt, subFields };
                                                });
                                                syncOptionsForField(field, nextOptions);
                                              }}
                                              className="text-red-500 hover:text-red-700"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        ))}
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            const currentOptions = getCurrentOptions(field);
                                            const nextOptions = currentOptions.map((opt, i) => {
                                              if (i !== index) return { ...opt };
                                              const subFields = [...(opt.subFields || [])];
                                              const currentSubField = { ...(subFields[subIndex] || {}) };
                                              const subOptions = [
                                                ...(currentSubField.optionsWithPricing || []),
                                                { label: `Option ${((currentSubField.optionsWithPricing || []).length || 0) + 1}`, price: 0, slots: null }
                                              ];
                                              currentSubField.optionsWithPricing = subOptions;
                                              currentSubField.options = subOptions
                                                .filter(o => o.label && o.label.trim() !== '')
                                                .map(o => o.label);
                                              subFields[subIndex] = currentSubField;
                                              return { ...opt, subFields };
                                            });
                                            syncOptionsForField(field, nextOptions);
                                          }}
                                        >
                                          <Plus className="w-3 h-3 mr-1" />
                                          Add Sub-option
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const currentOptions = getCurrentOptions(field);
                                    const nextOptions = currentOptions.map((opt, i) => {
                                      if (i !== index) return { ...opt };
                                      const nextSubFields = [
                                        ...(opt.subFields || []),
                                        {
                                          type: 'text',
                                          label: `Sub Field ${(opt.subFields || []).length + 1}`,
                                          required: false,
                                          placeholder: 'Enter value'
                                        }
                                      ];
                                      return { ...opt, subFields: nextSubFields };
                                    });
                                    syncOptionsForField(field, nextOptions);
                                  }}
                                  className="w-full"
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add Sub-field
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const currentOptions = field.optionsWithPricing || field.optionWithBlackLists || field.options?.map(opt => ({ label: opt, price: 0, slots: null, blackListedQrPurposes: [], subFields: [] })) || [];
                        const newOptions = [
                          ...currentOptions.map(opt => ({ 
                            ...opt, 
                            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
                          })), 
                          { label: `Option ${currentOptions.length + 1}`, price: 0, currency: 'INR', slots: null, blackListedQrPurposes: [], subFields: [] }
                        ];
                        const filteredOptions = newOptions.filter(opt => opt.label && opt.label.trim() !== '');
                        
                        // Create completely independent copies for each property
                        const optionsForPricing = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                        const optionsForBlacklist = newOptions.map(opt => ({ ...opt, blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] }));
                        
                        updateField(field.id, { 
                          optionsWithPricing: optionsForPricing,
                          optionWithBlackLists: optionsForBlacklist,
                          options: filteredOptions.map(opt => opt.label)
                        });
                      }}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Option
                    </Button>
                  </div>
                </div>
                
                {/* Show pricing summary */}
                {(field.optionsWithPricing || field.optionWithBlackLists)?.some(opt => opt.price > 0) && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Pricing Summary</h4>
                    <div className="text-xs text-blue-700 space-y-1">
                      {(field.optionsWithPricing || field.optionWithBlackLists || []).map((option, index) => (
                        <div key={index} className="flex justify-between">
                          <span>{option.label}</span>
                          <span className={option.price > 0 ? 'font-medium' : 'text-blue-500'}>
                            {option.price > 0 ? `${option.price}` : 'Free'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {field.type === 'file' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Accepted File Types
                </label>
                <Input
                  value={field.accept || ''}
                  onChange={(e) => updateField(field.id, { accept: e.target.value })}
                  placeholder="e.g., image/*, .pdf, .doc"
                />
              </div>
            )}

            {field.type === 'list' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price Per Item
                  </label>
                  <Input
                    type="number"
                    value={field.price || 0}
                    onChange={(e) => updateField(field.id, { price: parseFloat(e.target.value) || 0 })}
                    placeholder="Price charged per list item"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Each item added by the user will be charged this amount.
                  </p>
                </div>
                {field.price > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-900 mb-1">Pricing Info</h4>
                    <p className="text-xs text-blue-700">
                      {formatCurrency(field.price, 'INR')} per item — total = price × number of items added
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Blacklisted QR Purposes */}
            {qrScanPurposes.length > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Blacklisted QR Purposes
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select QR scan purposes that should NOT be allowed to modify this field
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-white">
                  {qrScanPurposes.map((purpose) => (
                    <div key={purpose.purpose} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`blacklist-${field.id}-${purpose.purpose}`}
                        checked={(field.blackedListedQrPurposes || []).includes(purpose.purpose)}
                        onChange={(e) => {
                          const currentBlacklist = field.blackedListedQrPurposes || [];
                          const newBlacklist = e.target.checked
                            ? [...currentBlacklist, purpose.purpose]
                            : currentBlacklist.filter(p => p !== purpose.purpose);
                          updateField(field.id, { blackedListedQrPurposes: newBlacklist });
                        }}
                        className="rounded border-gray-300"
                      />
                      <label 
                        htmlFor={`blacklist-${field.id}-${purpose.purpose}`} 
                        className="text-sm text-gray-700 flex-1 cursor-pointer"
                      >
                        <span className="font-medium">{purpose.name}</span>
                        <span className="text-gray-500 ml-2">({purpose.purpose})</span>
                      </label>
                    </div>
                  ))}
                </div>
                {(field.blackedListedQrPurposes || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {field.blackedListedQrPurposes.map((purposeId) => {
                      const purpose = qrScanPurposes.find(p => p.purpose === purposeId);
                      return purpose ? (
                        <span
                          key={purposeId}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800"
                        >
                          {purpose.name}
                          <button
                            onClick={() => {
                              const newBlacklist = field.blackedListedQrPurposes.filter(p => p !== purposeId);
                              updateField(field.id, { blackedListedQrPurposes: newBlacklist });
                            }}
                            className="ml-1 hover:text-red-900"
                          >
                            <XCircle className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingField(null);
                  // Only trigger onSave if autoSaveOnFieldEdit is true (for EventManagement with blacklist)
                  if (autoSaveOnFieldEdit && onSave) {
                    onSave(schema);
                  }
                }}
              >
                Done
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => removeField(field.id)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderFieldPreview = (field) => {
    const fieldConfig = FORM_FIELD_TYPES[field.type];
    if (!fieldConfig) return null;

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {fieldConfig.component === 'input' && (
          <Input
            type={fieldConfig.inputType}
            placeholder={field.placeholder}
            disabled
            className="bg-gray-50"
          />
        )}
        
        {fieldConfig.component === 'textarea' && (
          <Textarea
            placeholder={field.placeholder}
            disabled
            className="bg-gray-50"
            rows={3}
          />
        )}
        
        {fieldConfig.component === 'select' && (
          <div className="space-y-2">
            <Select disabled>
              <SelectTrigger className="bg-gray-50">
                <SelectValue placeholder={field.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {(field.optionsWithPricing || field.options?.map(opt => ({ label: opt, price: 0, slots: null })) || [])
                  .filter(option => option.label && option.label.trim() !== '')
                  .map((option, index) => (
                  <SelectItem key={index} value={option.label}>
                    <div className="flex justify-between items-center w-full">
                      <span>{option.label}</span>
                      {option.price > 0 && (
                        <span className="text-green-600 font-medium ml-2">{formatCurrency(option.price, option.currency || 'INR')}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.optionsWithPricing?.some(opt => opt.price > 0) && (
              <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                💰 This field has pricing options
              </div>
            )}
          </div>
        )}
        
        {fieldConfig.component === 'file' && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-gray-50">
            <p className="text-gray-500">File upload preview</p>
            <p className="text-xs text-gray-400 mt-1">
              Accepts: {field.accept || 'All files'}
            </p>
          </div>
        )}

        {fieldConfig.component === 'list' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input placeholder={field.placeholder || 'Enter item'} disabled className="bg-gray-50 flex-1" />
              <Button variant="outline" size="sm" disabled>Remove</Button>
            </div>
            <Button variant="outline" size="sm" disabled className="w-full">
              <Plus className="w-4 h-4 mr-1" /> Add More
            </Button>
            {field.price > 0 && (
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                {formatCurrency(field.price, 'INR')} per item
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Schema Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">Form Builder</CardTitle>
            {!hideMainSaveButton && (
            <div className="flex space-x-2">
              <Button variant="outline" onClick={handlePreview}>
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Save Schema
              </Button>
            </div>
            )}
          </div>
        </CardHeader>
        {draftKey && draftState && (
          <div className="mx-6 mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span>
              {draftState === 'restored' && 'Unsaved draft restored. Save the form to keep it on the server.'}
              {draftState === 'saved' && 'Draft saved in this browser. Save the form to keep it on the server.'}
              {draftState === 'error' && 'This browser could not store the draft. Save the form before leaving.'}
            </span>
            {draftState !== 'error' && (
              <Button type="button" variant="outline" size="sm" onClick={discardDraft}>Discard draft</Button>
            )}
          </div>
        )}
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schema Name
              </label>
              <Input
                value={schema.name}
                onChange={(e) => updateSchemaName(e.target.value)}
                placeholder="Enter schema name (e.g., Tech Conference Registration)"
                className="text-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Form Logo (optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">Shown instead of event logo when form is displayed. Recommended 1600 × 400 px (4:1), PNG/WebP under 500 KB.</p>
              <div className="flex gap-2">
                <Input
                  value={schema.formLogoUrl || ''}
                  onChange={(e) => updateSchemaFormLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="flex-1"
                />
                <label className="flex items-center px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
                  <Upload className="w-4 h-4 mr-1" />
                  {uploadingAsset === 'form_logo' ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFormSchemaAssetUpload('form_logo', f);
                      e.target.value = '';
                    }}
                    disabled={uploadingAsset === 'form_logo'}
                  />
                </label>
              </div>
              {schema.formLogoUrl && (
                <div className="mt-2">
                  <img src={schema.formLogoUrl} alt="Form logo preview" className="max-h-16 object-contain" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Form Background URL (optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">Background for the form container. Enter URL or upload image.</p>
              <div className="flex gap-2">
                <Input
                  value={schema.formBackgroundUrl || ''}
                  onChange={(e) => updateSchemaFormBackgroundUrl(e.target.value)}
                  placeholder="https://example.com/background.png"
                  className="flex-1"
                />
                <label className="flex items-center px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
                  <Upload className="w-4 h-4 mr-1" />
                  {uploadingAsset === 'form_background' ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFormSchemaAssetUpload('form_background', f);
                      e.target.value = '';
                    }}
                    disabled={uploadingAsset === 'form_background'}
                  />
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sections</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">
            Name the steps first, such as Personal Information or Select Interests. Each field then picks one from a list, and fields in the same section stay together.
          </p>
          {(schema.sections || []).map((section, index) => (
            <div key={index} className="flex gap-2">
              <Input placeholder="Section name" value={section || ''}
                onChange={(e) => commitSchema((prev) => {
                  const previous = (prev.sections || [])[index];
                  const name = e.target.value;
                  return {
                    ...prev,
                    sections: (prev.sections || []).map((item, i) => i === index ? name : item),
                    fields: (prev.fields || []).map((field) => field.section === previous ? { ...field, section: name } : field),
                  };
                })} />
              <Button type="button" variant="ghost" onClick={() => commitSchema((prev) => {
                const name = (prev.sections || [])[index];
                return {
                  ...prev,
                  sections: (prev.sections || []).filter((_, i) => i !== index),
                  fields: (prev.fields || []).map((field) => field.section === name ? { ...field, section: '' } : field),
                };
              })}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => commitSchema((prev) => ({ ...prev, sections: [...(prev.sections || []), ''] }))}>
            <Plus className="mr-1 h-4 w-4" />Add section
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Premium form settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            A pass is the card on the landing page, such as Delegate or Visitor, and it is Free, Paid or Approval. One pass holds the categories people book, each with its own price, such as Indian Delegate at ₹5,000 and Foreign Delegate at $200. Sales dates, capacity, day access and icons stay on the event.
          </p>
          {(schema.groups || []).length === 0 && <p className="text-sm text-gray-500">No passes yet. A form without passes stays a regular registration form.</p>}
          {(schema.groups || []).map((group, index) => {
            const groupKey = passId(group);
            const categories = (schema.passes || []).map((pass, passIndex) => ({ pass, passIndex })).filter(({ pass }) => pass.groupId === groupKey);
            const paid = (group.type || 'PAID') === 'PAID';
            return (
              <div key={index} className="space-y-3 rounded-lg border p-3">
                <div className="grid gap-2 sm:grid-cols-4">
                  <Input className="sm:col-span-2" placeholder="Pass, e.g. Delegate" value={group.name || ''}
                    onChange={(e) => commitSchema((prev) => {
                      const name = e.target.value;
                      const nextId = nextItemId(group, name);
                      const previousId = passId(group);
                      return {
                        ...prev,
                        groups: (prev.groups || []).map((item, i) => i === index ? { ...item, name, id: nextId || item.id } : item),
                        passes: nextId && previousId !== nextId
                          ? (prev.passes || []).map((pass) => ({ ...pass, groupId: pass.groupId === previousId ? nextId : pass.groupId }))
                          : prev.passes,
                      };
                    })} />
                  <select aria-label="Pass type" value={group.type || 'PAID'}
                    onChange={(e) => commitSchema((prev) => {
                      const type = e.target.value;
                      const id = passId(group);
                      return {
                        ...prev,
                        groups: prev.groups.map((item, i) => i === index ? { ...item, type } : item),
                        passes: (prev.passes || []).map((pass) => pass.groupId === id
                          ? { ...pass, type, price: type === 'PAID' ? (Number(pass.price) || 0) : 0 }
                          : pass),
                      };
                    })}
                    className="h-10 rounded-md border border-gray-300 bg-white px-2 text-sm">
                    <option value="FREE">Free</option>
                    <option value="PAID">Paid</option>
                    <option value="APPROVAL">Approval</option>
                  </select>
                  <Button type="button" variant="ghost" onClick={() => commitSchema((prev) => ({
                    ...prev,
                    groups: prev.groups.filter((_, i) => i !== index),
                    passes: (prev.passes || []).filter((pass) => pass.groupId !== groupKey),
                  }))}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
                <div className="space-y-2 pl-1">
                  <p className="text-sm font-medium text-gray-800">Categories</p>
                  {categories.map(({ pass, passIndex }) => (
                    <div key={passIndex} className="grid gap-2 sm:grid-cols-6">
                      <Input className="sm:col-span-3" placeholder="Category, e.g. Indian Delegate" value={pass.name || ''}
                        onChange={(e) => commitSchema((prev) => {
                          const name = e.target.value;
                          const nextId = nextPassId(prev.passes, passIndex, name, pass.groupId);
                          const shared = prev.passes.filter((item) => item.id && item.id === pass.id).length > 1;
                          return {
                            ...prev,
                            passes: prev.passes.map((item, i) => i === passIndex ? { ...item, name, id: nextId || item.id, type: group.type || 'PAID' } : item),
                            fields: !shared && pass.id && nextId && pass.id !== nextId
                              ? prev.fields.map((field) => ({ ...field, passIds: (field.passIds || []).map((id) => id === pass.id ? nextId : id) }))
                              : prev.fields,
                          };
                        })} />
                      <Input type="number" min="0" step="0.01" placeholder="Price" disabled={!paid} value={paid ? (pass.price ?? 0) : 0}
                        onChange={(e) => commitSchema((prev) => ({ ...prev, passes: prev.passes.map((item, i) => i === passIndex ? { ...item, price: Number(e.target.value) || 0 } : item) }))} />
                      <select aria-label="Category currency" value={pass.currency || 'INR'} disabled={!paid}
                        onChange={(e) => commitSchema((prev) => ({ ...prev, passes: prev.passes.map((item, i) => i === passIndex ? { ...item, currency: e.target.value } : item) }))}
                        className="h-10 rounded-md border border-gray-300 bg-white px-2 text-sm disabled:bg-gray-50">
                        {OPTION_CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
                      </select>
                      <Button type="button" variant="ghost" onClick={() => commitSchema((prev) => ({ ...prev, passes: prev.passes.filter((_, i) => i !== passIndex) }))}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                  {categories.length === 0 && <p className="text-sm text-gray-500">No categories under this pass yet.</p>}
                  <Button type="button" variant="outline" size="sm" onClick={() => commitSchema((prev) => ({
                    ...prev,
                    passes: [...(prev.passes || []), { ...emptyPass(), groupId: groupKey, type: group.type || 'PAID' }],
                  }))}>
                    <Plus className="mr-1 h-4 w-4" />Add category
                  </Button>
                </div>
              </div>
            );
          })}
          <Button type="button" variant="outline" size="sm" onClick={() => commitSchema((prev) => ({ ...prev, groups: [...(prev.groups || []), emptyCategory()] }))}>
            <Plus className="mr-1 h-4 w-4" />Add pass
          </Button>
          <p className="text-sm text-gray-600">
            On a dropdown, the price and the Dates button are added when the currency matches the category, including a price that changes on a date.
          </p>
        </CardContent>
      </Card>

      {/* Add Field Buttons */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Add Fields</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(FORM_FIELD_TYPES).map(([type, config]) => (
              <Button
                key={type}
                variant="outline"
                onClick={() => addField(type)}
                className="h-auto p-4 flex flex-col items-center space-y-2"
              >
                <div className="text-2xl">
                  {type === 'text' && '📝'}
                  {type === 'email' && '📧'}
                  {type === 'number' && '🔢'}
                  {type === 'date' && '📅'}
                  {type === 'dropdown' && '📋'}
                  {type === 'file' && '📎'}
                  {type === 'textarea' && '📄'}
                  {type === 'list' && '📑'}
                </div>
                <span className="text-sm capitalize">{type}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Form Fields */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Form Fields</CardTitle>
        </CardHeader>
        <CardContent>
          {schema.fields.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No fields added yet. Click on a field type above to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {schema.fields.map((field, index) => (
                <div key={field.id}>
                  <Card className="border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-600">
                            {field.type.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500">
                            {field.label}
                          </span>
                          {field.section && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{field.section}</span>
                          )}
                          {field.required && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingField(
                              editingField === field.id ? null : field.id
                            )}
                          >
                            {editingField === field.id ? 'Cancel' : 'Edit'}
                          </Button>
                          {index > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveField(index, index - 1)}
                            >
                              ↑
                            </Button>
                          )}
                          {index < schema.fields.length - 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveField(index, index + 1)}
                            >
                              ↓
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {renderFieldPreview(field)}
                    </CardContent>
                  </Card>
                  
                  {renderFieldEditor(field)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FormBuilder;
